import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ChevronDown, ChevronUp, Info, Radar, RefreshCw, UserPlus } from 'lucide-react';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useBackBookRadar } from './useBackBookRadar';
import MissingOriginalAmountsPanel from './MissingOriginalAmountsPanel';
import { breakdownLines, formatCentsAud, paidDownLabel, paidDownParts } from './radarFormat';
import type { RadarRow, RadarUnavailableReason } from './types';

/**
 * Back book radar: the caller's own active settled clients who may be ready to
 * buy again, ranked by a transparent score. The score's breakdown (each rule
 * and its points) is one click away on the score cell. The Refer action opens
 * the refer form with the client NAME only; the broker types the contact
 * details, so no contact detail is ever auto-filled without the broker seeing it.
 */

// Kept as named exports for existing importers; both are the shared formatters.
export { paidDownLabel };
export const formatCents = formatCentsAud;

const UNAVAILABLE_COPY: Record<RadarUnavailableReason, string> = {
  partner_network_disabled: 'Partner Referrals is not enabled for your company.',
  no_scope: 'Your account is not linked to a broker or office yet, so there are no clients to show.',
};

function formatTenure(years: number | null): string {
  if (years === null) return 'n/a';
  return `${years.toFixed(1)} yrs`;
}

// ─── Sorting (the LodgementRegister pattern: column descriptors + null-last compare) ───

type SortDirection = 'asc' | 'desc';
type SortValue = string | number | null;
type ColumnKey = 'client' | 'broker' | 'score' | 'tenure' | 'facilities' | 'balance' | 'paidDown';

interface Column {
  key: ColumnKey;
  label: string;
  align: 'left' | 'right';
  /** Direction a column opens in: names A-Z, figures largest first. */
  firstDirection: SortDirection;
  sortValue: (row: RadarRow, amountsHidden: boolean) => SortValue;
}

export const RADAR_COLUMNS: Column[] = [
  { key: 'client', label: 'Client', align: 'left', firstDirection: 'asc', sortValue: (r) => r.displayName },
  { key: 'broker', label: 'Broker', align: 'left', firstDirection: 'asc', sortValue: (r) => r.broker },
  { key: 'score', label: 'Score', align: 'right', firstDirection: 'desc', sortValue: (r) => r.score },
  { key: 'tenure', label: 'Client for', align: 'right', firstDirection: 'desc', sortValue: (r) => r.tenureYears },
  { key: 'facilities', label: 'Facilities', align: 'right', firstDirection: 'desc', sortValue: (r) => r.activeFacilities },
  {
    key: 'balance',
    label: 'Balance',
    align: 'right',
    firstDirection: 'desc',
    sortValue: (r, hidden) => (hidden ? null : r.totalBalanceCents),
  },
  {
    key: 'paidDown',
    label: 'Paid down',
    align: 'left',
    firstDirection: 'desc',
    sortValue: (r, hidden) => (hidden ? null : r.paidDownCents),
  },
];

/** Nullish always sorts last, whichever way the column is pointing. */
export function compareSortValues(a: SortValue, b: SortValue, direction: SortDirection): number {
  if (a === b) return 0;
  if (a === null || a === '') return 1;
  if (b === null || b === '') return -1;
  const sign = direction === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
  return String(a).localeCompare(String(b), 'en-AU') * sign;
}

/** Sort a copy; ties keep the server's ranking (score order), so the result is deterministic. */
export function sortRadarRows(
  rows: RadarRow[],
  sort: { column: ColumnKey; direction: SortDirection },
  amountsHidden: boolean,
): RadarRow[] {
  const col = RADAR_COLUMNS.find((c) => c.key === sort.column) ?? RADAR_COLUMNS[2];
  return rows
    .map((row, index) => ({ row, index }))
    .sort(
      (a, b) =>
        compareSortValues(col.sortValue(a.row, amountsHidden), col.sortValue(b.row, amountsHidden), sort.direction) ||
        a.index - b.index,
    )
    .map((x) => x.row);
}

// ─── Score cell with its breakdown popover ───

const POPOVER_MAX_WIDTH_REM = 18;

const ScoreCell: React.FC<{ row: RadarRow }> = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const lines = breakdownLines(row);
  const baseId = `radar-score-${row.clientKey}`;

  const close = useCallback(() => setOpen(false), []);

  const toggle = () => {
    if (open) return setOpen(false);
    // Fixed positioning so the table's horizontal scroll container can never
    // clip the popover; clamped inside the viewport at phone width.
    const r = btnRef.current?.getBoundingClientRect();
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const vw = window.innerWidth || 1024;
    const width = Math.min(POPOVER_MAX_WIDTH_REM * rem, vw - rem);
    const left = r ? Math.max(rem / 2, Math.min(r.right - width, vw - width - rem / 2)) : rem / 2;
    setPos({ top: r ? r.bottom + rem / 3 : 0, left, width });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        btnRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popRef.current?.contains(t) && !btnRef.current?.contains(t)) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <div className="inline-flex items-center justify-end gap-1.5">
      <span className="font-semibold text-white">{row.score}</span>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? `${baseId}-pop` : undefined}
        aria-describedby={`${baseId}-sr`}
        aria-label={`Score breakdown for ${row.displayName}`}
        className="rounded p-0.5 text-gray-500 hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {/* Always in the DOM for assistive tech; the popover is the visual form. */}
      <span id={`${baseId}-sr`} className="sr-only">
        {lines.map((l) => `${l.label}: ${l.points} points`).join('. ')}
      </span>
      {open && pos && (
        <div
          ref={popRef}
          id={`${baseId}-pop`}
          role="dialog"
          aria-label={`Score breakdown for ${row.displayName}`}
          data-testid="score-breakdown"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
          className="z-50 rounded-lg border border-gray-600 bg-gray-900 p-3 text-left text-xs shadow-xl"
        >
          <div className="mb-2 font-semibold text-gray-200">Why this client scores {row.score}</div>
          {lines.length === 0 ? (
            <p className="text-gray-400">No signal scored for this client.</p>
          ) : (
            <ul className="space-y-1">
              {lines.map((l) => (
                <li key={l.label} className="flex items-baseline justify-between gap-3">
                  <span className="text-gray-300">{l.label}</span>
                  <span className="shrink-0 tabular-nums text-gray-400">{l.points > 0 ? `+${l.points}` : '0'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ─── The tab ───

const BackBookRadarTab: React.FC = () => {
  const parentCtx = useOutletContext<{ userData?: { companyId?: string } | null } | null>();
  const companyId = parentCtx?.userData?.companyId;
  const navigate = useNavigate();
  const [brokerFilter, setBrokerFilter] = useState<string | null>(null);
  const [showMissing, setShowMissing] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [sort, setSort] = useState<{ column: ColumnKey; direction: SortDirection }>({ column: 'score', direction: 'desc' });
  const { data, loading, error, reload } = useBackBookRadar(companyId, brokerFilter);

  const available = data && data.available ? data : null;
  const sortedRows = useMemo(
    () => (available ? sortRadarRows(available.rows, sort, available.amountsHidden) : []),
    [available, sort],
  );

  const onSortChange = (column: ColumnKey) => {
    const col = RADAR_COLUMNS.find((c) => c.key === column)!;
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: col.firstDirection },
    );
  };

  const refer = (row: RadarRow) => {
    navigate('/partner-referrals/refer', { state: { prefill: { clientName: row.displayName } } });
  };

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-4" aria-busy="true">
        <SkeletonTable rows={8} columns={7} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
        Could not load the back book radar. {error}
        <button type="button" onClick={() => void reload()} className="ml-3 underline hover:text-red-200">
          Try again
        </button>
      </div>
    );
  }

  if (!data) return null;

  if (!data.available) {
    return (
      <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-6 text-sm text-gray-300" role="status">
        <div className="flex items-center gap-2 font-medium text-white">
          <Radar className="h-4 w-4 text-blue-400" />
          Back book radar unavailable
        </div>
        <p className="mt-2">{UNAVAILABLE_COPY[data.reason]}</p>
      </div>
    );
  }

  const showBrokerFilter = data.brokers.length > 1 || brokerFilter !== null;
  // hideLoanAmounts users never see the missing-originals link or panel.
  const missingCount = data.amountsHidden ? null : data.missingOriginalCount;

  return (
    <div className="space-y-4">
      {/* Toolbar: left-aligned, directly above the table. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="radar-toolbar">
        {showBrokerFilter && (
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <span>Broker</span>
            <select
              aria-label="Filter by broker"
              value={brokerFilter ?? ''}
              onChange={(e) => setBrokerFilter(e.target.value || null)}
              className="rounded-md border border-gray-600 bg-gray-800 px-2 py-1.5 text-sm text-white"
            >
              <option value="">All brokers</option>
              {data.brokers.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          onClick={() => void reload()}
          aria-label="Refresh radar"
          className="rounded-md border border-gray-600 p-2 text-gray-300 hover:bg-gray-800 hover:text-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'opacity-50' : ''}`} />
        </button>
        {missingCount !== null && missingCount > 0 && (
          <button
            type="button"
            onClick={() => setShowMissing((v) => !v)}
            aria-expanded={showMissing}
            className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-200 hover:bg-amber-500/20"
          >
            {missingCount} {missingCount === 1 ? 'loan is' : 'loans are'} missing an original amount
            {showMissing ? ' (hide)' : ': complete them'}
          </button>
        )}
      </div>

      {/* Compact one-line note; the detail expands on demand. */}
      <div className="text-xs text-gray-400" data-testid="radar-note">
        <button
          type="button"
          onClick={() => setNoteOpen((v) => !v)}
          aria-expanded={noteOpen}
          aria-controls="radar-note-detail"
          className="inline-flex items-center gap-1.5 hover:text-gray-200"
        >
          <Info className="h-3.5 w-3.5 text-blue-400" />
          <span>Estimated values are not used.</span>
          <span className="underline">{noteOpen ? 'Hide details' : 'Details'}</span>
        </button>
        {noteOpen && (
          <p id="radar-note-detail" className="mt-1.5 max-w-3xl text-gray-400">
            Every figure comes from your lenders&apos; commission statements; where a lender did not report a balance the
            value shows as n/a. Where no original loan amount was reported, paid down is measured from the first trail
            statement and shown as &quot;at least&quot; unless that statement was within three months of settlement.
            Property value, equity and rate expiry are not considered.
            {data.asOfMonth ? ` Book as of ${data.asOfMonth}.` : ''}
            {data.amountsHidden ? ' Loan amounts are hidden for your account, so the score ignores them.' : ''}
          </p>
        )}
      </div>

      {showMissing && missingCount !== null && (
        <MissingOriginalAmountsPanel companyId={companyId} onSaved={() => void reload()} />
      )}

      {sortedRows.length === 0 ? (
        <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-6 text-sm text-gray-300" role="status">
          None of your active clients meet the radar&apos;s signals yet.
        </div>
      ) : (
        // The table scrolls inside its own container, so phone width never
        // scrolls the page horizontally.
        <div className="max-w-full overflow-x-auto rounded-xl border border-gray-700">
          <table className="min-w-full divide-y divide-gray-700 text-sm">
            <thead className="bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-400">
              <tr>
                {RADAR_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      sort.column === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                    className={`whitespace-nowrap px-3 py-2 ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    <button
                      type="button"
                      onClick={() => onSortChange(column.key)}
                      className="group inline-flex items-center gap-1.5 uppercase hover:text-white"
                      title={`Click to sort by ${column.label}`}
                    >
                      {column.label}
                      <span className="flex-shrink-0">
                        {sort.column === column.key ? (
                          sort.direction === 'asc' ? (
                            <ChevronUp className="h-4 w-4 text-blue-400" aria-hidden="true" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-blue-400" aria-hidden="true" />
                          )
                        ) : (
                          <span className="flex flex-col -space-y-2.5 opacity-40 group-hover:opacity-70" aria-hidden="true">
                            <ChevronUp className="h-3 w-3" />
                            <ChevronDown className="h-3 w-3" />
                          </span>
                        )}
                      </span>
                    </button>
                  </th>
                ))}
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Action</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {sortedRows.map((row) => {
                const paid = paidDownParts(row);
                return (
                  <tr key={row.clientKey} data-testid="radar-row" className="hover:bg-gray-800/40">
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-white">{row.displayName}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-300">{row.broker ?? 'n/a'}</td>
                    <td className="px-3 py-2 text-right">
                      <ScoreCell row={row} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-gray-300">{formatTenure(row.tenureYears)}</td>
                    <td className="px-3 py-2 text-right text-gray-300">{row.activeFacilities}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-300">
                      {data.amountsHidden ? 'Hidden' : formatCentsAud(row.totalBalanceCents)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2" data-testid="paid-down-cell">
                      {data.amountsHidden ? (
                        <span className="text-gray-300">Hidden</span>
                      ) : (
                        <>
                          <span className="block tabular-nums text-white">{paid.amount}</span>
                          {paid.basis && <span className="block text-xs text-gray-500">{paid.basis}</span>}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => refer(row)}
                        aria-label={`Refer ${row.displayName}`}
                        className="inline-flex items-center gap-1 rounded-md border border-blue-500/40 px-2 py-1 text-xs font-medium text-blue-300 hover:bg-blue-500/15"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                        Refer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data.truncated && (
        <p className="text-xs text-gray-400">
          Showing the top {data.rows.length} of {data.totalCandidates} clients by score.
        </p>
      )}
    </div>
  );
};

export default BackBookRadarTab;
